import React, { useEffect, useMemo, useState } from "react";
import { getDirection } from "../../../helper";
import { CgMoreO } from "react-icons/cg";
import NewPlaceholder from "../../../assets/placeholder_moving.gif";
import PlaceholderImage from "../../../assets/placeholder_static.png";
import { inTimeColors, speedColor } from "../../../const";
import { vehicleByVehNo } from "../../../api/vehicleapi";
import {
  getDocumentByOpeAndRefAndDocCatAndFileNameDownscaled,
  getDownscaledFile,
} from "../../../api/documentUpload";
import { formatFromJsonDate } from "../../../dateConfig";
import { useDispatch, useSelector } from "react-redux";
import Toggle from "../../input/Toggle";
import { missionControlActions } from "../../../store/mission-control/mission-control-slice";
import useDirections from "../../../hooks/useDirections";
import Tooltip from "../../input/Tooltip";
import { getJobLocations } from "../../../api/missioncontrolApi";
import { LabelCellStyle } from "../../renderer/StyleRenderer";

const VehiclePopup = ({
  socketData,
  jobVehicleInfoToggle,
  ds,
  handleAdvancedDirectionResponse,
}) => {
  const {
    terminalId,
    vehicleNo,
    speed,
    direction,
    dateTime,
    latitude,
    longitude,
  } = socketData;
  const [vehicleImage, setVehicleImage] = useState(null);
  const [vehicleDetails, setVehicleDetails] = useState(null);
  const dispatch = useDispatch();
  const showPath = useSelector((state) => state.missionControl.showPath);
  const showDirection = useSelector(
    (state) => state.missionControl.showDirection,
  );
  const [jobLocationData, setJobLocationData] = useState(null);
  const [isJobLocationLoading, setIsJobLocationLoading] = useState(false);

  const advancedDirection = useSelector(
    (state) => state.missionControl.advancedDirections[terminalId],
  );

  const jobLocation = useSelector(
    (state) => state.missionControl.jobLocation[terminalId],
  );

  const jobData = useSelector(
    (state) => state.missionControl.jobInfo[terminalId],
  );

  const avoidHighways = useSelector(
    (state) => state.missionControl.mapSettings.avoidHighways,
  );

  useEffect(() => {
    (async () => {
      if (jobLocation) {
        setJobLocationData(jobLocation);
      } else {
        setIsJobLocationLoading(true);
        const data = await getJobLocations(jobData.jobId);

        let startLoc = null;
        let endLoc = null;

        for (let loc of data) {
          if (loc.locTypId === 1) {
            startLoc = loc.locaId;
          }

          if (loc.locTypId === 2) {
            endLoc = loc.locaId;
          }
        }

        const currentLocationData = {
          locations: data,
          sameStartAndEnd: startLoc === endLoc,
        };

        dispatch(
          missionControlActions.addJobLocation({
            terminalId: terminalId,
            data: currentLocationData,
          }),
        );
        setJobLocationData(currentLocationData);
        setIsJobLocationLoading(false);
      }
    })();
  }, [dispatch, jobData?.jobId, jobLocation, terminalId]);

  const toolTipStyle = {
    height: "50px",
    display: "flex",
    flexDirection: "column",
    gap: "5px",
  };

  const toolTipRowStyle = {
    display: "flex",
    alignItems: "center",
  };

  const toolTipLabel = {
    minWidth: "120px",
    textAlign: "start",
    margin: 0,
  };

  const toolTipContent = { minWidth: "220px", margin: 0 };

  useEffect(() => {
    (async () => {
      const vehicleData = await vehicleByVehNo(vehicleNo);
      setVehicleDetails(vehicleData.vehicleDetail);

      const response =
        await getDocumentByOpeAndRefAndDocCatAndFileNameDownscaled(
          4,
          vehicleData.vehicleDetail.vehId,
          1,
          1,
        );
      if (response && response.length > 0) {
        const data = response[0];
        const fileData = await getDownscaledFile(data.docId);

        const file = new File([fileData], data.docName, {
          type: fileData.type,
        });
        setVehicleImage(file);
      }
    })();
  }, [vehicleNo]);

  const origin = useMemo(
    () => ({ lat: latitude, lng: longitude }),
    [latitude, longitude],
  );

  const destination = useMemo(
    () => ({ lat: jobData?.locaLat, lng: jobData?.locaLon }),
    [jobData?.locaLat, jobData?.locaLon],
  );

  const locationInfo = useMemo(
    () => jobLocationData?.locations,
    [jobLocationData?.locations],
  );

  const { routeSummary } = useDirections({
    origin: origin,
    destination: destination,
    showRoutes: showDirection[terminalId],
    ds,
    advancedDirection: advancedDirection,
    locationInfoLoading: isJobLocationLoading,
    locationInfo,
    nextLocation: jobData?.locaId,
    handleAdvancedDirectionResponse,
    terminalId,
    avoidHighways,
    jobId: jobData?.jobId,
  });

  const isETAPassed =
    (jobData?.locaEta - Date.now()) / 1000 < routeSummary?.duration?.value;

  return (
    <div
      className={
        ds === 1 ? "mission-control-map-popup-gm" : "mission-control-map-popup"
      }
      style={{
        display: "flex",
        gap: "10px",
        padding: "10px",
        flexDirection: "column",
      }}
    >
      {jobData && vehicleDetails && (
        <CgMoreO
          size={25}
          className={
            ds === 1
              ? "mission-control-map-popup-action-gm"
              : "mission-control-map-popup-action"
          }
          onClick={() => {
            if (jobData) {
              jobVehicleInfoToggle(
                jobData.jobId,
                vehicleNo,
                vehicleDetails.vehModelName,
                vehicleDetails.vehManufacturerName,
              );
            }
          }}
        />
      )}
      <div className="mission-control-map-popup-main">
        <img
          className="mission-control-map-popup-image"
          src={
            vehicleImage
              ? URL.createObjectURL(vehicleImage)
              : speed > 0
                ? NewPlaceholder
                : PlaceholderImage
          }
          alt={"veh"}
        />
        <div>
          <h3>
            {vehicleDetails
              ? vehicleDetails.vehManufacturerName
                ? vehicleDetails.vehManufacturerName
                : "N/A"
              : "N/A"}
            <br />
            {vehicleDetails
              ? vehicleDetails.vehModelName
                ? vehicleDetails.vehModelName
                : "N/A"
              : "N/A"}
            ({vehicleNo})
          </h3>
          <p>
            Last Updated at - <br />
            {dateTime ? formatFromJsonDate(dateTime) : "N/A"}
          </p>
          {speed > 0 ? (
            <div
              style={{
                display: "flex",
                gap: "5px",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "5px" }}
              >
                <span>At </span>
                <span
                  className="mission-control-map-popup-label"
                  style={{
                    backgroundColor:
                      speed === 0
                        ? speedColor.STATIONARY
                        : speed > 60
                          ? speedColor.HIGH
                          : speedColor.LOW,
                  }}
                >
                  {speed}
                </span>
                <span>km/h heading </span>
              </div>
              <div
                style={{ display: "flex", alignItems: "center", gap: "5px" }}
              >
                <span className="mission-control-map-popup-label">
                  {getDirection(direction)}
                </span>
              </div>
            </div>
          ) : (
            <span className="mission-control-map-popup-label">
              Vehicle Stationary
            </span>
          )}
        </div>
      </div>
      <div className="ui divider"></div>
      <>
        <div className="mission-control-map-popup-footer">
          <div className="mission-control-map-popup-footer-segment-2">
            <h4>
              {jobData ? (jobData.jobCode ? jobData.jobCode : "N/A") : "N/A"}
            </h4>
          </div>
          <div className="mission-control-map-popup-footer-segment-3">
            <h4>
              <LabelCellStyle
                width={"180px"}
                height={"30px"}
                color={
                  jobData?.locationTypes === "Pickup"
                    ? "#2196F3"
                    : jobData?.locationTypes === "Arrived"
                      ? "#4caf50"
                      : "#FFCC33"
                }
                value={jobData?.locationTypes}
              />
            </h4>
          </div>
        </div>
        <div className="ui divider"></div>
        <div className="mission-control-map-popup-footer">
          <div className="mission-control-map-popup-footer-segment">
            <h4>
              {jobData ? (jobData.locaName ? jobData.locaName : "N/A") : "N/A"}
            </h4>
          </div>
          <div
            className="mission-control-map-popup-footer-segment"
            style={{ width: "10%" }}
          >
            <h2>IN</h2>
          </div>
          <div className="mission-control-map-popup-footer-segment">
            <h3
              style={{
                color: isETAPassed ? inTimeColors.LATE : inTimeColors.IN_TIME,
              }}
            >
              <Tooltip
                trigger={
                  <div>
                    {routeSummary
                      ? routeSummary.duration
                        ? routeSummary.duration.text
                        : "N/A"
                      : "N/A"}
                  </div>
                }
              >
                <div style={toolTipStyle}>
                  <div style={toolTipRowStyle}>
                    <div style={toolTipLabel}>Expected ETA -</div>
                    <h4 style={toolTipContent}>
                      {jobData?.locaEta
                        ? formatFromJsonDate(jobData?.locaEta)
                        : "N/A"}
                    </h4>
                  </div>
                  <div style={toolTipRowStyle}>
                    <div style={toolTipLabel}>Predicted Time -</div>
                    <h4 style={toolTipContent}>
                      {routeSummary
                        ? formatFromJsonDate(
                            routeSummary?.duration?.value * 1000 + Date.now(),
                          )
                        : "N/A"}
                    </h4>
                  </div>
                </div>
              </Tooltip>
            </h3>
          </div>
        </div>
        <div className="ui divider"></div>
        <div className="mission-control-map-popup-footer">
          <div className="mission-control-map-popup-footer-segment">
            <h5>Path</h5>
          </div>
          <div className="mission-control-map-popup-footer-segment">
            <Toggle
              value={showPath[terminalId]}
              onChange={() =>
                dispatch(missionControlActions.togglePath(terminalId))
              }
            />
          </div>
          <div className="mission-control-map-popup-footer-segment">
            <h5>Direction</h5>
          </div>
          <div className="mission-control-map-popup-footer-segment">
            <Toggle
              value={showDirection[terminalId]}
              onChange={() =>
                dispatch(missionControlActions.toggleDirections(terminalId))
              }
            />
          </div>
          <div className="mission-control-map-popup-footer-segment">
            <h5>Advanced Direction</h5>
          </div>
          <div className="mission-control-map-popup-footer-segment">
            <Toggle
              value={advancedDirection}
              onChange={() =>
                dispatch(
                  missionControlActions.toggleAdvancedDirections(terminalId),
                )
              }
            />
          </div>
        </div>
      </>
    </div>
  );
};

export default VehiclePopup;
